import { Container, Tabs } from "@mantine/core";
import { Link } from "react-router-dom";
import { ReactNode } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/feature-toggle-names";
import { StrengthenReferralHeader } from "./components/strengthen-referral-header/strengthen-referral-header";
import { useHasSurveySchema } from "@/hooks/use-has-survey-schema";
import styles from "./strengthen-referral.module.scss";

interface StrengthenReferralProps {
  appealId: string;
  activeTab: string;
  tabContent: ReactNode;
}

export function StrengthenReferral({
  appealId,
  activeTab,
  tabContent,
}: StrengthenReferralProps) {
  const { hasSurveySchema, loading } = useHasSurveySchema(appealId);
  const showSurveyTabs = !loading && hasSurveySchema;
  const showLetterOfMedicalNecessityTab =
    useFeatureFlagEnabled(
      FeatureToggle.LETTER_OF_MEDICAL_NECESSITY_GENERATION,
    ) === true;

  return (
    <Container fluid className={styles.mainContainer}>
      <StrengthenReferralHeader appealId={appealId} />

      <Tabs value={activeTab} className={styles.tabs}>
        <Tabs.List>
          <Tabs.Tab
            value="patient"
            renderRoot={(props) => (
              <Link {...props} to={`/appeals/${appealId}/strengthen/patient`} />
            )}
          >
            Patient
          </Tabs.Tab>
          <Tabs.Tab
            value="insurance"
            renderRoot={(props) => (
              <Link
                {...props}
                to={`/appeals/${appealId}/strengthen/insurance`}
              />
            )}
          >
            Insurance
          </Tabs.Tab>
          <Tabs.Tab
            value="appeal-destination"
            renderRoot={(props) => (
              <Link
                {...props}
                to={`/appeals/${appealId}/strengthen/appeal-destination`}
              />
            )}
          >
            Appeal Destination
          </Tabs.Tab>
          <Tabs.Tab
            value="documents"
            renderRoot={(props) => (
              <Link
                {...props}
                to={`/appeals/${appealId}/strengthen/documents`}
              />
            )}
          >
            Supporting documents
          </Tabs.Tab>
          {showSurveyTabs && (
            <Tabs.Tab
              value="prescriber-information"
              renderRoot={(props) => (
                <Link
                  {...props}
                  to={`/appeals/${appealId}/strengthen/prescriber-information`}
                />
              )}
            >
              Prescriber Information
            </Tabs.Tab>
          )}
          {showSurveyTabs && (
            <Tabs.Tab
              value="medical-info"
              renderRoot={(props) => (
                <Link
                  {...props}
                  to={`/appeals/${appealId}/strengthen/medical-info/diagnosis`}
                />
              )}
            >
              Medical Info
            </Tabs.Tab>
          )}
          {showSurveyTabs && (
            <Tabs.Tab
              value="patient-survey"
              renderRoot={(props) => (
                <Link
                  {...props}
                  to={`/appeals/${appealId}/strengthen/patient-survey`}
                />
              )}
            >
              Patient Survey
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="submission-details"
            renderRoot={(props) => (
              <Link
                {...props}
                to={`/appeals/${appealId}/strengthen/submission-details`}
              />
            )}
          >
            Submission Details
          </Tabs.Tab>
          {showLetterOfMedicalNecessityTab && (
            <Tabs.Tab
              value="letter-of-medical-necessity"
              renderRoot={(props) => (
                <Link
                  {...props}
                  to={`/appeals/${appealId}/strengthen/letter-of-medical-necessity`}
                />
              )}
            >
              LOMN
            </Tabs.Tab>
          )}
        </Tabs.List>
        <Tabs.Panel value={activeTab}>{tabContent}</Tabs.Panel>
      </Tabs>
    </Container>
  );
}
